import QRCode from 'qrcode';
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { navigate } from '../lib/router';
import { baht, main } from '../lib/text';
import type { Bill } from '../lib/types';
import { Back } from '../ui/icons';
import type { GuestState } from './GuestApp';
import { Header } from './Header';

const TIP_PERCENT = [0, 5, 10, 15];

/** /t/bill: "check, please" — the bill, the guest's own tip choice, and the restaurant's PromptPay QR. */
export function GuestBill({ s }: { s: GuestState }) {
  const [bill, setBill] = useState<Bill | null>(null);
  const [qr, setQr] = useState('');
  const [custom, setCustom] = useState('');
  const [picked, setPicked] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const token = s.guest.token;

  const show = useCallback(async (b: Bill) => {
    setBill(b);
    setQr(b.promptpay ? await QRCode.toString(b.promptpay.payload, { type: 'svg', margin: 1 }) : '');
  }, []);
  useEffect(() => {
    void api('POST', '/guest/bill/request', { token }).catch(() => undefined);
    void api<Bill>('GET', '/guest/bill', { token }).then(show, (e: ApiError) => setErr(e.message));
  }, [token, show, s.orders]);

  const tip = async (tipSatang: number) => {
    setErr(null);
    try { await show(await api<Bill>('PUT', '/guest/tip', { token, body: { tipSatang } })); } catch (e) { setErr((e as Error).message); }
  };

  const pct = (p: number) => Math.round(((bill?.subtotalSatang ?? 0) * p) / 100 / 100) * 100; // whole baht
  // Two presets can round to the same baht on a small bill: highlight the one the guest tapped, else the first match.
  const pressed = bill && (picked !== null && pct(picked) === bill.tipSatang ? picked : TIP_PERCENT.find((p) => pct(p) === bill.tipSatang));
  return (
    <main className="screen">
      <Header s={s} sub="เช็คบิล · Your bill" />
      <div className="row" style={{ padding: '4px 20px 12px' }}>
        <button className="icon-btn" onClick={() => navigate('/t/orders')} aria-label="Back"><Back /></button>
        <h1 className="display" style={{ margin: 0, fontSize: 24, flex: 1, paddingLeft: 12 }}>เช็คบิล · Bill</h1>
      </div>
      <section className="quest">
        {!bill ? <div className="spinner" role="status" /> : (
          <>
            {bill.unresolved.length > 0 && (
              <div className="warn">ยังมี {bill.unresolved.length} รายการที่ยังไม่ได้เสิร์ฟ · {bill.unresolved.length} dish(es) still on the way</div>
            )}
            <ul className="ticket" style={{ listStyle: 'none', margin: 0, gap: 6 }}>
              {bill.lines.map((l) => (
                <li key={l.orderId + l.lineId} className="row" style={{ textDecoration: l.status === 'cancelled' ? 'line-through' : undefined, color: l.status === 'cancelled' ? 'var(--ink-2)' : undefined }}>
                  <span>{l.qty}× {main(l.name)}</span><span>{baht(l.unitSatang * l.qty)}</span>
                </li>
              ))}
              <li className="row" style={{ borderTop: '2px dashed var(--ink)', paddingTop: 6 }}><span>รวม · Subtotal</span><span>{baht(bill.subtotalSatang)}</span></li>
            </ul>

            <div className="ticket">
              <span className="label">ทิปให้พนักงาน · Tip for the team</span>
              <div className="choices" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                {TIP_PERCENT.map((p) => (
                  <button key={p} className="choice" aria-pressed={pressed === p} onClick={() => { setPicked(p); void tip(pct(p)); }}>
                    <span>{p ? `${p}%` : 'ไม่ใส่'}</span><small>{p ? baht(pct(p)) : 'No tip'}</small>
                  </button>
                ))}
              </div>
              <div className="row" style={{ gap: 8 }}>
                <input className="note" inputMode="numeric" placeholder="จำนวนอื่น (บาท) · Other amount (฿)" value={custom} onChange={(e) => setCustom(e.target.value.replace(/\D/g, '').slice(0, 5))} />
                <button className="cta light" style={{ width: 'auto', padding: '10px 16px' }} disabled={!custom} onClick={() => { setPicked(null); void tip(Number(custom) * 100); }}>OK</button>
              </div>
            </div>

            <div className="ticket">
              <div className="row"><span>ทิป · Tip</span><span>{baht(bill.tipSatang)}</span></div>
              <div className="row display" style={{ fontSize: 24 }}><span>ยอดชำระ · Total</span><span>{baht(bill.totalSatang)}</span></div>
            </div>

            {qr ? (
              <div className="ticket" style={{ alignItems: 'center' }}>
                <span className="label">สแกนจ่ายพร้อมเพย์ · Pay with PromptPay</span>
                <div className="qr" style={{ border: '3px solid var(--ink)' }} role="img" aria-label={`PromptPay QR for ${baht(bill.totalSatang)}`} dangerouslySetInnerHTML={{ __html: qr }} />
                <span className="muted" style={{ fontSize: 13, textAlign: 'center' }}>ยอด {baht(bill.totalSatang)} · จ่ายแล้วแจ้งพนักงาน · show staff once paid</span>
              </div>
            ) : (
              <div className="ticket"><span>พนักงานกำลังมาเก็บเงิน · A staff member is on the way to take payment.</span></div>
            )}
            {err && <div className="warn" role="alert">{err}</div>}
          </>
        )}
      </section>
    </main>
  );
}
